<template>
    <div>
        <!-- Form Area -->
        <div class="space-y-6 p-3">
            <!-- Wall Size -->
            <div>
                <label class="block text-lg font-semibold">
                    What size is your wall?
                </label>
                <div class="grid sm:grid-cols-2 gap-x-4">
                    <div class="flex-1 mt-2">
                        <label class="block text-sm font-medium mb-1" for="height">
                            Height (meters)
                        </label>
                        <input type="number" min="0" v-model.number="height" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400" placeholder="e.g. 2.5" required>
                    </div>
                    <div class="flex-1 mt-2">
                        <label class="block text-sm font-medium mb-1" for="length">
                            Length (meters)
                        </label>
                        <input type="number" min="0" v-model.number="length" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400" placeholder="e.g. 4.0" required>
                    </div>
                </div>
            </div>

            <!-- Brick Type -->
            <div>
                <label class="block text-lg font-semibold mb-2">
                    What size are your bricks?
                </label>
                <div>
                    <label class="inline-flex items-center">
                        <input type="radio" value="standard" v-model="brickType" class="form-radio text-blue-500">
                        <span class="ml-2">Standard brick (215 x 65 mm)</span>
                    </label>
                </div>
                <div>
                    <label class="inline-flex items-center">
                        <input type="radio" value="block" v-model="brickType" class="form-radio text-blue-500">
                        <span class="ml-2">Block (440 x 215 mm)</span>
                    </label>
                </div>
                <div>
                    <label class="inline-flex items-center">
                        <input type="radio" value="custom" v-model="brickType" class="form-radio text-blue-500">
                        <span class="ml-2">Custom size</span>
                    </label>
                </div>
                <div v-if="brickType === 'custom'" class="space-y-2 mt-4">
                    <div class="flex space-x-4">
                        <div class="flex-1">
                            <label class="block text-sm font-medium mb-1" for="customHeight">
                                Custom brick height (mm)
                            </label>
                        <input type="number" min="0" v-model.number="customHeight" class="w-full p-2 border border-gray-300 rounded-lg" placeholder="e.g. 100">
                        </div>
                        <div class="flex-1">
                            <label class="block text-sm font-medium mb-1" for="customLength">
                                Custom brick length (mm)
                            </label>
                        <input type="number" min="0" v-model.number="customLength" class="w-full p-2 border border-gray-300 rounded-lg" placeholder="e.g. 200">
                        </div>
                    </div>
                </div>
            </div>

            <!-- Mortar Joint -->
            <div>
                <label class="block text-lg font-semibold mb-2">
                    How thick are your mortar joints (mm)?
                </label>
                <input type="number" min="0" v-model.number="mortarJoint" class="w-full p-2 border border-gray-300 rounded-lg" placeholder="10">
            </div>
        </div>

        <!-- Output Area -->
        <div v-if="showResults" class="mt-8 p-4 bg-gray-100 rounded-lg">
            <div class="hidden sm:table w-full border-separate">
                <table class="table-auto w-full">
                    <tbody>
                        <tr class="font-medium text-lg">
                            <td class="w-1 text-nowrap pr-6">
                                Area covered:
                            </td>
                            <td>
                                <span class="font-bold">
                                    {{ area.toLocaleString(undefined, {minimumFractionDigits: 3}) }}
                                </span>
                                <span class="text-gray-600 ml-1">
                                    m²
                                </span>
                            </td>
                        </tr>
                        <tr class="font-medium text-lg">
                            <td class="w-1 text-nowrap pr-6">
                                Number of bricks/blocks required:
                            </td>
                            <td class="font-bold">
                                {{ brickCount.toLocaleString() }}
                            </td>
                        </tr>
                        <tr class="font-medium text-sm">
                            <td class="w-1 text-nowrap pr-6">
                                Mortar joint width:
                            </td>
                            <td>
                                <span class="font-bold">
                                    {{ mortarJoint }}
                                </span>
                                <span class="text-gray-600 ml-1">
                                    mm
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Mobile version -->
            <div class="sm:hidden space-y-3">
                <div>
                    <div class="font-medium text-lg">
                        Area covered:
                    </div>
                    <div>
                        <span class="font-bold">
                            {{ area.toLocaleString(undefined, {minimumFractionDigits: 3}) }}
                        </span>
                        <span class="text-gray-600 ml-1">
                            m²
                        </span>
                    </div>
                </div>
                <div>
                    <div class="font-medium text-lg">
                        Number of bricks/blocks required:
                    </div>
                    <div class="font-bold">
                        {{ brickCount.toLocaleString() }}
                    </div>
                </div>
                <div>
                    <div class="font-medium text-sm">
                        Mortar joint width:
                    </div>
                    <div>
                        <span class="font-bold">
                            {{ mortarJoint }}
                        </span>
                        <span class="text-gray-600 ml-1">
                            mm
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            height: null,
            length: null,
            brickType: null,
            customHeight: null,
            customLength: null,
            mortarJoint: 10,
            brickSizes: {
                'standard': { height: 65, length: 215 },
                'block': { height: 215, length: 440 }
            }
        };
    },

    watch: {
        height(value) {
            if (value < 0) this.height = 0;
        },
        length(value) {
            if (value < 0) this.length = 0;
        },
        customHeight(value) {
            if (value < 0) this.customHeight = 0;
        },
        customLength(value) {
            if (value < 0) this.customLength = 0;
        },
        mortarJoint(value) {
            if (value < 0) this.mortarJoint = 0;
            }
    },

    computed: {
        area() {
            if (this.height && this.length) {
                return this.height * this.length;
            }
            return 0;
        },
        brickSize() {
            if (this.brickType === 'custom' && this.customHeight && this.customLength) {
                // Convert mm to meters squared
                return (this.customHeight + this.mortarJoint) * (this.customLength + this.mortarJoint) / 1000000;
            } else if (this.brickType && this.brickSizes[this.brickType]) {
                const { height, length } = this.brickSizes[this.brickType];
                // Convert mm to meters squared
                return (height + this.mortarJoint) * (length + this.mortarJoint) / 1000000;
            }
            return 0;
        },
        brickCount() {
            if (this.area > 0 && this.brickSize > 0) {
                return Math.ceil(this.area / this.brickSize);
            }
            return 0;
        },
        showResults() {
            return this.height && this.length && this.brickType && (this.brickType !== 'custom' || (this.customHeight && this.customLength));
        }
    }
};
</script>
